import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import 'react-phone-input-2/lib/style.css'
import { Icon } from "@iconify/react";
import TailwindLayout from 'TailwindcssComponents/TailwindLayout';
import TopBar from 'TailwindcssComponents/TopBar';
import FooterTailwind from 'TailwindcssComponents/Footer';

export default function AddUser() {
  const apiUrl = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();
  const [organizationImagePreview, setOrganizationImagePreview] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState('No File Choosen')
  const [isLoading, setIsLoading] = useState(false);

  const handleOrganizationImageChange = (event) => {
    const file = event.currentTarget.files[0];
    newUser.setFieldValue('logo', file);
    setOrganizationImagePreview(URL.createObjectURL(file));
  };

  const onSubmit = async (values, { resetForm }) => {
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('firstName', values.firstName);
      formData.append('lastName', values.lastName);
      formData.append('email', values.email);
      formData.append('phone', values.phone);
      formData.append('country', values.country);
      formData.append('city', values.city);
      formData.append('password', values.password);
      formData.append('logo', values.logo);

      const response = await axios.post(`${apiUrl}auth-service/auth`, formData,
        {
          headers: { 'Content-Type': `multipart/form-data` }
        });
      if (response.data?.success) {
        toast.success(response.data?.message || 'User has been successfully created!');

        resetForm();
        setTimeout(() => {
          navigate('/allUsers');
          setIsLoading(false);
        }, 1500);
      } else {
        throw new Error('Failed to create user');
      }
    } catch (error) {
      console.error(error.response.data?.message || error.message);
      toast.error(error.response.data?.message || error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const newUser = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      password: '',
      country: '',
      city: '',
      logo: '',
    },
    validationSchema: Yup.object({
      firstName: Yup.string()
        .required('Required'),
      lastName: Yup.string()
        .required('Required'),
      phone: Yup.string()
        .required('Required')
        .matches(/^\+?[1-9]\d{1,14}$/, 'Mobile number is not valid')
        .min(10, 'Not a valid phone number')
        .max(15, 'Not a valid phone number'),
      email: Yup.string()
        .required('Required')
        .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Enter a valid email')
        .email('Enter a valid email'),
      password: Yup.string()
        .required('Required')
        .min(8, 'Password must be at least 8 characters long'),
      logo: Yup.mixed()
        .test('fileSize', "File size must be less than 1MB", (value) => {
          return value && value.size <= 1 * 1024 * 1024; // 1MB
        })
        .test('fileFormat', 'Unsupported Format', (value) => {
          return value && ['image/jpg', 'image/jpeg', 'image/png'].includes(value.type);
        }),
    }),
    onSubmit,
  });

  return (
    <TailwindLayout>
      <TopBar />
      <div className="flex justify-center items-center min-h-screen bg-gray-50 p-6">
        <div className="w-full max-w-lg bg-white rounded-lg shadow-md border border-gray-100 overflow-hidden mb-12">

          {/* Form content */}
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 border-b pb-2">Add New User</h2>
            <form onSubmit={newUser.handleSubmit}>
              <div className="space-y-4 mt-6">
                {/* First Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    placeholder="First Name"
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    {...newUser.getFieldProps('firstName')}
                  />
                  {newUser.touched.firstName && newUser.errors.firstName && <div className="text-red-500">{newUser.errors.firstName}</div>}
                </div>

                {/* Last Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    placeholder="Last Name"
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    {...newUser.getFieldProps('lastName')}
                  />
                  {newUser.touched.lastName && newUser.errors.lastName && <div className="text-red-500">{newUser.errors.lastName}</div>}
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="e.g. +1234567890"
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    {...newUser.getFieldProps('phone')}
                  />
                  {newUser.touched.phone && newUser.errors.phone && <div className="text-red-500">{newUser.errors.phone}</div>}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    placeholder="e.g. abdc@zmail.com"
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    {...newUser.getFieldProps('email')}
                  />
                  {newUser.touched.email && newUser.errors.email && <div className="text-red-500">{newUser.errors.email}</div>}
                </div>

                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password
                  </label>
                  <input
                    type="password"
                    name='password'
                    placeholder="Password"
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    {...newUser.getFieldProps('password')}
                  />
                  {newUser.touched.password && newUser.errors.password && <div className="text-red-500">{newUser.errors.password}</div>}
                </div>

                {/* country */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country
                  </label>
                  <input
                    type="country"
                    name='country'
                    placeholder="Country"
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    {...newUser.getFieldProps('country')}
                  />
                </div>

                {/* city */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City
                  </label>
                  <input
                    type="city"
                    name='city'
                    placeholder="city"
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    {...newUser.getFieldProps('city')}
                  />
                </div>

                <div className="pt-4 flex flex-col md:flex-row items-center justify-between gap-4">
                  {/* Image Upload Section */}
                  <div className="flex items-center w-full md:w-auto">
                    <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mr-3 relative">
                      {organizationImagePreview ? (
                        <>
                          <img
                            src={organizationImagePreview}
                            alt="Organization Logo"
                            className="w-full h-full object-cover rounded-lg"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setOrganizationImagePreview(null);
                              newUser.setFieldValue('orgImage', null);
                              newUser.setFieldTouched('orgImage', true);
                            }}
                            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-red-500 hover:bg-red-600 text-white rounded-full w-4 h-4 flex items-center justify-center shadow-lg transition-colors"
                            aria-label="Remove image"
                          >
                            <Icon icon="material-symbols-light:close-rounded" className="text-lg" />
                          </button>
                        </>
                      ) : (
                        <Icon icon="iconoir:add-media-image" className="text-gray-400 text-2xl" />
                      )}
                    </div>
                    <div>
                      <p className="text-xs italic  text-gray-600 mb-1">Please upload logo, size less than 1MB, 1:1, JPG/JPEG/PNG format</p>
                      <div className="flex space-x-2">
                        <label className="px-3 py-1 bg-white border border-blue-500 text-blue-500 rounded hover:bg-blue-50 cursor-pointer text-sm">
                          {organizationImagePreview ? 'Change Image' : 'Choose Image'}
                          <input
                            type="file"
                            className="hidden"
                            onChange={(e) => {
                              handleOrganizationImageChange(e);
                            }}
                          />
                        </label>
                        {newUser.touched.logo && newUser.errors.logo && (
                          <p className="text-red-500 text-sm mt-1">{newUser.errors.logo}</p>
                        )}
                        {/* <span className="text-sm text-gray-500 py-1">{selectedFileName}</span> */}
                      </div>
                    </div>
                  </div>

                  {/* Save Button */}
                  <button
                    disabled={isLoading}
                    type="submit"
                    className="w-full md:w-24 bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    {isLoading ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
              {isLoading && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white p-4 rounded-lg flex flex-col items-center shadow-lg">
                    <svg
                      className="animate-spin h-8 w-8 text-blue-600 mb-3"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                      ></path>
                    </svg>

                    <p className="text-sm font-medium text-gray-700">Saving...</p>
                  </div>
                </div>
              )}

            </form>
          </div>
        </div>
      </div>
      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <FooterTailwind />
    </TailwindLayout>
  )
}